import { Injectable } from '@angular/core';
import { ChipExpressionEventProvider } from './chip.expression.event.provider';
import { RootParametersProvider, ValuesExchangeProvider } from '@nrwl/common-lib';
import { DRILL_DIRECTION, DrillDirectionEvent } from '@nrwl/smart-search';
import { SimpleChipExpression, ExpertChipExpression } from '@nrwl/query-emitter';
import { cloneDeep, find, forEach, union } from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class DrillEventsHandler {
  constructor(
    readonly eventProvider: ChipExpressionEventProvider,
    readonly rootParams: RootParametersProvider,
    readonly valuesExchange: ValuesExchangeProvider
  ) {
    this.valuesExchange.registerInterest(
      DRILL_DIRECTION,
      value => this.handleDrillEvent(value),
      true
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  async handleDrillEvent(value: any) {
    const expr = this.transformToExpression(value);
    const event = await this.eventProvider.buildEvent(expr.expression);
    this.rootParams.notifyListeners('fuzzy_filter', event);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  transformToExpression(userDrillAction: DrillDirectionEvent): any {
    if (userDrillAction.type === 'new') {
      return {
        expression: userDrillAction.expressions
      };
    } else if (userDrillAction.type === 'reset') {
      // contrary to 'new', 'clear' resets search filter to empty expression
      return {
        expression: []
      };
    } else if (userDrillAction.type === 'append') {
      // this is very tricky. For example, consider this use case:
      // query exists 'site = London'. Then somebody appends query. The combined query is
      // site = Londong and app = FTP. Then somebody appends query once again.
      // combined query becomes:
      // site = London and app = FTP and app = HTTP. This is plain wrong. 'app' criteria should have been
      // merged together: site = London and app in (FTP, HTTP).
      const currentFilter = this.rootParams.getLastKnownValue('fuzzy_filter');
      return {
        expression: alterQuery(
          currentFilter ? currentFilter.human_readable || currentFilter.expression : [],
          userDrillAction.expressions
        )
      };
    } else if (userDrillAction.type === 'readonly') {
      return {
        readonlyChipState: true,
        expression: alterQuery(
          userDrillAction.expressions,
          this.rootParams.getLastKnownValue('fuzzy_filter')
        )
      };
    }
  }
}

// an alternative to drill event that constructs readonly+append
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
export function constructReadOnlyExpression(expressions: [], currentExpression: [] = []) {
  return {
    readOnlyChipState: true,
    expression: alterQuery(expressions, currentExpression)
  };
}

export function alterQuery(
  currentQuery: SimpleChipExpression[] = [],
  chips: SimpleChipExpression[] = []
): SimpleChipExpression[] {
  let result = cloneDeep(currentQuery);
  if (!result || result.length === 0) {
    result = chips;
    return result;
  }

  forEach(chips, chip => {
    const newKeyword = chip.keyword.toUpperCase();

    // special business logic quirks with Host and User
    if (newKeyword === 'HOST' || newKeyword === 'USER') {
      const chipToEdit = find(result, item => {
        const testKeyword = item.keyword.toUpperCase();
        return testKeyword === 'HOST' || testKeyword === 'USER';
      });
      if (chipToEdit) {
        // in case User is replaced with Host, and vice versa
        chipToEdit.keyword = chip.keyword;
        // Notice we also override operator
        chipToEdit.operator = chip.operator;
        chipToEdit.operands = chip.operands;
        return;
      }
    }

    // ideally, we should investigate chips, and in case we find chip with same keyword and operator, we append operand to that chip.
    let found = false;
    for (let i = result.length - 1; i >= 0; i--) {
      const expression = result[i];

      if (expression.keyword === chip.keyword && expression.operator === chip.operator) {
        expression.operands = union(expression.operands, chip.operands);
        found = true;
        break;
      }
    }
    if (!found) {
      chip = new ExpertChipExpression(chip.keyword, chip.operator, chip.operands, 'AND');
      result.push(chip);
    }
  });

  return result;
}
